/* Theme toggle Web Component — light/dark switcher for District v7.
 *
 * Behavior:
 *   - On first visit: respects window.matchMedia('(prefers-color-scheme: dark)')
 *   - Manual toggle: persists choice to localStorage 'tbw-theme' (light|dark)
 *   - Once user has manually toggled, OS preference is no longer auto-applied
 *     (manual override is sticky across sessions)
 *
 * The actual theme application happens in two places:
 *   1. Inline FOUC-prevention <script> in <head> of theme.liquid (runs before
 *      first paint, no flash of wrong theme on load)
 *   2. This component (handles user click + sync across multiple toggles +
 *      sync across browser tabs via 'storage' event)
 *
 * Component API:
 *   <theme-toggle></theme-toggle>
 *
 * No attributes; the component reads/writes html[data-theme] + localStorage.
 *
 * Built with createElement (no innerHTML) to satisfy the project's
 * security-guidance hook (no XSS surface even though strings are static).
 */

class ThemeToggle extends HTMLElement {
  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
    this._onStorage = this._onStorage.bind(this);
    this._onSchemeChange = this._onSchemeChange.bind(this);
    this._mql = null;
  }

  connectedCallback() {
    this._render();
    this.addEventListener('click', this._onClick);
    window.addEventListener('storage', this._onStorage);

    if (window.matchMedia) {
      this._mql = window.matchMedia('(prefers-color-scheme: dark)');
      // Only auto-apply OS preference if user hasn't manually chosen
      if (this._mql.addEventListener) {
        this._mql.addEventListener('change', this._onSchemeChange);
      }
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    window.removeEventListener('storage', this._onStorage);
    if (this._mql && this._mql.removeEventListener) {
      this._mql.removeEventListener('change', this._onSchemeChange);
    }
  }

  _render() {
    const theme = document.documentElement.getAttribute('data-theme') || 'light';
    const isDark = theme === 'dark';

    // Build button
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'theme-toggle__btn';
    btn.setAttribute('aria-label',
      isDark ? 'Switch to light mode' : 'Switch to dark mode');
    btn.setAttribute('aria-pressed', String(isDark));
    btn.setAttribute('title',
      isDark ? 'Switch to light mode' : 'Switch to dark mode');

    // Sun icon (visible in dark mode = "click to go light")
    const sun = this._sunIcon();
    sun.classList.add('theme-toggle__icon', 'theme-toggle__icon--sun');
    sun.setAttribute('aria-hidden', 'true');

    // Moon icon (visible in light mode = "click to go dark")
    const moon = this._moonIcon();
    moon.classList.add('theme-toggle__icon', 'theme-toggle__icon--moon');
    moon.setAttribute('aria-hidden', 'true');

    btn.appendChild(sun);
    btn.appendChild(moon);

    // Visually hidden label for screen readers (some SR's don't read aria-label)
    const sr = document.createElement('span');
    sr.className = 'sr-only';
    sr.textContent = isDark ? 'Switch to light mode' : 'Switch to dark mode';
    btn.appendChild(sr);

    // Inject scoped styles once per component instance
    if (!this._stylesInjected) {
      this._injectStyles();
      this._stylesInjected = true;
    }

    // Replace existing children (idempotent on re-render)
    while (this.firstChild) this.removeChild(this.firstChild);
    this.appendChild(btn);
  }

  _sunIcon() {
    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '20');
    svg.setAttribute('height', '20');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');

    const circle = document.createElementNS(ns, 'circle');
    circle.setAttribute('cx', '12');
    circle.setAttribute('cy', '12');
    circle.setAttribute('r', '4');
    svg.appendChild(circle);

    const rays = [
      [12, 2, 12, 5], [12, 19, 12, 22],
      [2, 12, 5, 12], [19, 12, 22, 12],
      [4.93, 4.93, 7.05, 7.05], [16.95, 16.95, 19.07, 19.07],
      [4.93, 19.07, 7.05, 16.95], [16.95, 7.05, 19.07, 4.93],
    ];
    rays.forEach(([x1, y1, x2, y2]) => {
      const line = document.createElementNS(ns, 'line');
      line.setAttribute('x1', x1);
      line.setAttribute('y1', y1);
      line.setAttribute('x2', x2);
      line.setAttribute('y2', y2);
      svg.appendChild(line);
    });
    return svg;
  }

  _moonIcon() {
    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '20');
    svg.setAttribute('height', '20');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');

    const path = document.createElementNS(ns, 'path');
    path.setAttribute('d', 'M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z');
    svg.appendChild(path);
    return svg;
  }

  _injectStyles() {
    if (document.getElementById('tbw-theme-toggle-styles')) return;
    const style = document.createElement('style');
    style.id = 'tbw-theme-toggle-styles';
    style.textContent = [
      'theme-toggle { display: inline-flex; align-items: center; }',
      '.theme-toggle__btn {',
      '  display: inline-flex; align-items: center; justify-content: center;',
      '  width: 36px; height: 36px;',
      '  padding: 0; margin: 0 0.5rem; background: transparent; border: 0;',
      '  color: var(--header-text-color, currentColor); cursor: pointer;',
      '  border-radius: 50%;',
      '  transition: background-color 150ms ease, color 150ms ease;',
      '}',
      '.theme-toggle__btn:hover, .theme-toggle__btn:focus-visible {',
      '  background-color: var(--header-text-20-color, rgba(0,0,0,0.05));',
      '  outline: none;',
      '}',
      '.theme-toggle__btn:focus-visible {',
      '  box-shadow: 0 0 0 2px var(--primary-accent-color, currentColor);',
      '}',
      '.theme-toggle__icon {',
      '  display: block; width: 20px; height: 20px;',
      '  transition: transform 200ms ease, opacity 200ms ease;',
      '}',
      '.theme-toggle__icon--sun { display: none; }',
      'html[data-theme="dark"] .theme-toggle__icon--sun { display: block; }',
      'html[data-theme="dark"] .theme-toggle__icon--moon { display: none; }',
      '@media (prefers-reduced-motion: reduce) {',
      '  .theme-toggle__btn, .theme-toggle__icon { transition: none; }',
      '}',
    ].join('\n');
    document.head.appendChild(style);
  }

  _onClick(ev) {
    if (!ev.target.closest('button')) return;
    const current = document.documentElement.getAttribute('data-theme') || 'light';
    const next = current === 'dark' ? 'light' : 'dark';
    this._applyTheme(next, { persist: true, animate: true });
  }

  _onStorage(ev) {
    // Sync across tabs
    if (ev.key !== 'tbw-theme' || !ev.newValue) return;
    if (ev.newValue !== 'dark' && ev.newValue !== 'light') return;
    this._applyTheme(ev.newValue, { persist: false, animate: true });
  }

  _onSchemeChange(ev) {
    // Respect OS change ONLY if user hasn't manually toggled
    if (localStorage.getItem('tbw-theme')) return;
    this._applyTheme(ev.matches ? 'dark' : 'light', { persist: false, animate: true });
  }

  _applyTheme(theme, opts) {
    const html = document.documentElement;
    if (opts && opts.animate) {
      html.classList.add('theme-toggle-anim');
      window.setTimeout(() => html.classList.remove('theme-toggle-anim'), 220);
    }
    html.setAttribute('data-theme', theme);
    if (opts && opts.persist) {
      try { localStorage.setItem('tbw-theme', theme); } catch (e) { /* private mode */ }
    }
    this._render();
    document.dispatchEvent(new CustomEvent('tbw:theme-change', { detail: { theme } }));
  }
}

if (!customElements.get('theme-toggle')) {
  customElements.define('theme-toggle', ThemeToggle);
}
